import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Briefcase, User, Check, ChevronDown, Plus, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// RW-BUILD-004 — single-login workspace switcher (Owner ⇄ Provider).
// Shows only workspaces available to the account; offers to add the missing one.
export default function WorkspaceSwitcher() {
  const { token, applyWorkspaceToken } = useAuth();
  const [ws, setWs] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/api/auth/workspaces`, { headers })
      .then((r) => { if (active) setWs(r.data); })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!ws || ws.activeWorkspace === 'admin') return null;

  const active = ws.activeWorkspace; // 'owner' | 'provider'
  const activeLabel = active === 'provider'
    ? (ws.provider?.businessName || 'Provider Workspace')
    : 'Owner Workspace';

  const switchTo = async (target) => {
    if (target === active) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/switch-workspace`, { workspace: target }, { headers });
      applyWorkspaceToken(data.token, data.workspace);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not switch workspace');
      setBusy(false);
    }
  };

  const addProvider = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/add-provider-business`, {}, { headers });
      toast.success('Provider business added');
      applyWorkspaceToken(data.token, 'provider');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add provider business');
      setBusy(false);
    }
  };

  const activateOwner = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/activate-owner`, {}, { headers });
      toast.success('Owner workspace activated');
      applyWorkspaceToken(data.token, 'owner');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not activate owner workspace');
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="workspace-switcher">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" />
            : (active === 'provider' ? <Briefcase className="h-4 w-4" /> : <User className="h-4 w-4" />)}
          <span className="max-w-[140px] truncate">{activeLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ws.hasOwner && (
          <DropdownMenuItem onClick={() => switchTo('owner')} data-testid="switch-owner">
            <User className="h-4 w-4 mr-2" /> Owner Workspace
            {active === 'owner' && <Check className="h-4 w-4 ml-auto text-primary" />}
          </DropdownMenuItem>
        )}
        {ws.hasProvider && (
          <DropdownMenuItem onClick={() => switchTo('provider')} data-testid="switch-provider">
            <Briefcase className="h-4 w-4 mr-2" /> {ws.provider?.businessName || 'Provider Workspace'}
            {active === 'provider' && <Check className="h-4 w-4 ml-auto text-primary" />}
          </DropdownMenuItem>
        )}
        {(!ws.hasProvider || !ws.hasOwner) && <DropdownMenuSeparator />}
        {!ws.hasProvider && (
          <DropdownMenuItem onClick={addProvider} data-testid="add-provider-business">
            <Plus className="h-4 w-4 mr-2" /> Add a Provider Business
          </DropdownMenuItem>
        )}
        {!ws.hasOwner && (
          <DropdownMenuItem onClick={activateOwner} data-testid="activate-owner">
            <Plus className="h-4 w-4 mr-2" /> Activate Owner Workspace
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
